import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { NotificationService, AppNotification, NotificationType } from '../shared/access/notification.service';
import { ConnectionStatusBadgeComponent } from '../shared/connection-status-badge.component';

const TYPE_META: Record<NotificationType, { icon: string; color: string }> = {
  orderUpdated: { icon: 'bi-arrow-repeat', color: 'var(--bs-primary)' },
  emailSent: { icon: 'bi-envelope-check', color: 'var(--bs-success)' },
  orderStatusUpdated: { icon: 'bi-activity', color: 'var(--topbar-accent)' }
};

@Component({
  selector: 'of-notification-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, ConnectionStatusBadgeComponent],
  template: `
    <li class="nav-item dropdown">
      <button
        class="of-tbar__icon-btn of-nd__btn"
        type="button"
        data-bs-toggle="dropdown"
        data-bs-auto-close="true"
        aria-expanded="false"
        title="Notifications"
        (click)="onOpen()"
      >
        <i class="bi bi-bell"></i>
        @if (notificationService.unreadCount() > 0) {
          <span class="of-nd__badge">{{
            notificationService.unreadCount() > 99 ? '99+' : notificationService.unreadCount()
          }}</span>
        }
        <of-connection-status [overlay]="true" />
      </button>

      <div class="dropdown-menu dropdown-menu-end of-nd__panel">
        <div class="of-nd__inner">
          <div class="of-nd__header">
            <span class="of-nd__title">Notifications</span>
            <div class="d-flex gap-1">
              @if (notificationService.notifications().length > 0) {
                <button
                  class="of-nd__action-btn"
                  type="button"
                  (click)="$event.stopPropagation(); notificationService.clear()"
                >
                  Clear all
                </button>
              }
            </div>
          </div>

          <div class="of-nd__list">
            @if (notificationService.notifications().length === 0) {
              <div class="of-nd__empty">
                <i class="bi bi-bell-slash of-nd__empty-icon"></i>
                <span>No notifications</span>
              </div>
            }
            @for (n of notificationService.notifications(); track n.id) {
              <div class="of-nd__item" [class.of-nd__item--unread]="!n.read">
                <i class="bi {{ iconFor(n) }} of-nd__item-icon" [style.color]="colorFor(n)"></i>
                <div class="of-nd__item-body">
                  <span class="of-nd__item-msg">{{ n.message }}</span>
                  <span class="of-nd__item-time">{{ n.timestamp | date: 'HH:mm:ss' }}</span>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </li>
  `,
  styles: [
    `
      .of-nd__btn {
        position: relative;
      }

      .of-nd__badge {
        background: var(--bs-danger);
        border-radius: 9px;
        color: #fff;
        font-size: 0.6rem;
        font-weight: 700;
        line-height: 1;
        min-width: 16px;
        padding: 2px 4px;
        pointer-events: none;
        position: absolute;
        right: 1px;
        text-align: center;
        top: 1px;
      }

      .of-nd__panel {
        border: 1px solid var(--topbar-border);
        border-radius: 0.5rem;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        min-width: 340px;
        padding: 0;
      }

      .of-nd__inner {
        display: flex;
        flex-direction: column;
        max-height: 480px;
        overflow: hidden;
      }

      .of-nd__header {
        align-items: center;
        border-bottom: 1px solid var(--topbar-border);
        display: flex;
        flex-shrink: 0;
        justify-content: space-between;
        padding: 0.6rem 0.85rem;
      }

      .of-nd__title {
        color: var(--topbar-text);
        font-size: 0.8rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }

      .of-nd__action-btn {
        background: transparent;
        border: none;
        color: var(--topbar-muted);
        cursor: pointer;
        font-size: 0.72rem;
        padding: 2px 6px;
        border-radius: 4px;
        transition:
          color 120ms,
          background 120ms;

        &:hover {
          background: var(--topbar-hover);
          color: var(--topbar-text);
        }
      }

      .of-nd__list {
        overflow-y: auto;
        flex: 1;
      }

      .of-nd__empty {
        align-items: center;
        color: var(--topbar-muted);
        display: flex;
        flex-direction: column;
        font-size: 0.8rem;
        gap: 0.5rem;
        padding: 2rem 1rem;
        text-align: center;
      }

      .of-nd__empty-icon {
        font-size: 1.5rem;
        opacity: 0.4;
      }

      .of-nd__item {
        align-items: flex-start;
        border-bottom: 1px solid var(--topbar-border);
        display: flex;
        gap: 0.6rem;
        padding: 0.55rem 0.85rem;
        transition: background 120ms;

        &:last-child {
          border-bottom: none;
        }
      }

      .of-nd__item--unread {
        background: var(--topbar-hover);
      }

      .of-nd__item-icon {
        flex-shrink: 0;
        font-size: 0.85rem;
        margin-top: 2px;
      }

      .of-nd__item-body {
        display: flex;
        flex-direction: column;
        gap: 0.1rem;
        min-width: 0;
      }

      .of-nd__item-msg {
        color: var(--topbar-text);
        font-size: 0.78rem;
        line-height: 1.35;
        white-space: normal;
        word-break: break-word;
      }

      .of-nd__item-time {
        color: var(--topbar-muted);
        font-size: 0.68rem;
      }
    `
  ]
})
export class NotificationDrawerComponent {
  protected readonly notificationService = inject(NotificationService);

  protected onOpen(): void {
    setTimeout(() => this.notificationService.markAllRead(), 300);
  }

  protected iconFor(n: AppNotification): string {
    return TYPE_META[n.type].icon;
  }

  protected colorFor(n: AppNotification): string {
    return TYPE_META[n.type].color;
  }
}
